import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  getShopifyStatus, 
  connectShopify, 
  disconnectShopify,
  getShopifyOrders,
  createInvoiceFromShopify
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ShoppingBag,
  Link2,
  Unlink,
  RefreshCw,
  FileText,
  CheckCircle,
  Clock,
  DollarSign,
  Loader2,
  AlertCircle,
  ExternalLink,
  HelpCircle,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

const ShopifyPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ connected: false });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('any');
  const [creatingInvoice, setCreatingInvoice] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Form state
  const [storeUrl, setStoreUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await getShopifyStatus();
      setStatus(res.data);
      if (res.data.connected) {
        loadOrders();
      }
    } catch (error) {
      console.error('Error loading status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    setLoadingOrders(true);
    try {
      const res = await getShopifyOrders(selectedFilter, 50);
      setOrders(res.data.orders || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleConnect = async () => {
    if (!storeUrl || !accessToken) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setConnecting(true);
    try {
      const res = await connectShopify(storeUrl, apiKey, accessToken);
      toast.success(`Connecté à ${res.data.shop_name}`);
      setConnectDialogOpen(false);
      setStoreUrl('');
      setApiKey('');
      setAccessToken('');
      loadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Déconnecter Shopify ?')) return;
    
    try {
      await disconnectShopify();
      toast.success('Shopify déconnecté');
      setStatus({ connected: false });
      setOrders([]);
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const handleCreateInvoice = async (orderId) => {
    setCreatingInvoice(orderId);
    try {
      const res = await createInvoiceFromShopify(orderId);
      toast.success(res.data.message);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreatingInvoice(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const res = await import('../lib/api').then(m => m.default.post('/shopify/sync-all'));
      const { synced_count, error_count } = res.data;
      if (synced_count > 0) {
        toast.success(`${synced_count} facture(s) créée(s) automatiquement`);
      } else {
        toast.info('Aucune nouvelle commande à facturer');
      }
      if (error_count > 0) {
        toast.warning(`${error_count} erreur(s) lors de la synchronisation`);
      }
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const getStatusBadge = (financialStatus) => {
    const config = {
      paid: { label: 'Payée', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
      pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
      refunded: { label: 'Remboursée', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
      partially_refunded: { label: 'Remb. partiel', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
      voided: { label: 'Annulée', color: 'bg-slate-100 text-slate-700 dark:bg-zinc-800 dark:text-zinc-400' },
    };
    const c = config[financialStatus] || config.pending;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="shopify-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-[#96bf48]" />
            Shopify
          </h1>
          <p className="text-slate-500 dark:text-zinc-400">Importez vos commandes Shopify</p>
        </div>
        {status.connected ? (
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={handleSyncAll}
              disabled={syncing}
              className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              data-testid="sync-all-btn"
            >
              {syncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync toutes les commandes
            </Button>
            <Button 
              variant="outline" 
              onClick={loadOrders}
              disabled={loadingOrders}
              className="dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingOrders ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button 
              variant="outline"
              onClick={handleDisconnect}
              className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Déconnecter
            </Button>
          </div>
        ) : (
          <Button 
            onClick={() => setConnectDialogOpen(true)}
            className="bg-[#96bf48] hover:bg-[#7fa73d] text-white"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Connecter Shopify
          </Button>
        )}
      </motion.div>

      {/* Connection Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="max-w-md dark:bg-[#111113] dark:border-white/10">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#96bf48]" />
              Connecter Shopify
            </DialogTitle>
            <DialogDescription className="dark:text-zinc-400">
              Connectez votre boutique Shopify pour importer vos commandes
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label className="dark:text-zinc-300">URL de votre boutique *</Label>
              <Input
                value={storeUrl}
                onChange={(e) => setStoreUrl(e.target.value)}
                placeholder="ma-boutique.myshopify.com"
                className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
                data-testid="shopify-store-url"
              />
              <p className="text-xs text-slate-500 dark:text-zinc-500 mt-1">
                Ex: ma-boutique ou ma-boutique.myshopify.com
              </p>
            </div>
            <div>
              <Label className="dark:text-zinc-300">Access Token *</Label>
              <Input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder="shpat_xxxxxxxxxxxxxxxx"
                className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
                data-testid="shopify-access-token"
              />
            </div>
            <div>
              <Label className="dark:text-zinc-300">API Key (optionnel)</Label>
              <Input
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                className="dark:bg-[#1a1a1c] dark:border-white/10 dark:text-white"
              />
            </div>
            
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-2">
                <HelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium mb-1">Comment obtenir ces informations ?</p>
                  <p className="text-xs">
                    Consultez notre <Link to="/app/faq" className="underline" onClick={() => setConnectDialogOpen(false)}>guide FAQ</Link> pour créer une App privée Shopify.
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={handleConnect}
              disabled={connecting}
              className="w-full bg-[#96bf48] hover:bg-[#7fa73d] text-white"
              data-testid="shopify-connect-btn"
            >
              {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
              Connecter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Content */}
      {status.connected ? (
        <>
          {/* Stats */}
          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="dark:bg-[#111113] dark:border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#96bf48]/10 flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-[#96bf48]" />
                </div>
                <div>
                  <p className="text-2xl font-bold dark:text-white">{orders.length}</p>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Commandes</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-[#111113] dark:border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold dark:text-white">
                    {orders.filter(o => o.financial_status === 'paid').length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Payées</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-[#111113] dark:border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold dark:text-white">
                    {orders.filter(o => o.invoiced).length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Facturées</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dark:bg-[#111113] dark:border-white/10">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold dark:text-white">
                    {orders.filter(o => !o.invoiced && o.financial_status === 'paid').length}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">À facturer</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Filter */}
          <motion.div variants={itemVariants} className="flex items-center gap-4">
            <Select value={selectedFilter} onValueChange={(v) => { setSelectedFilter(v); }}>
              <SelectTrigger className="w-[200px] dark:bg-[#111113] dark:border-white/10 dark:text-white">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent className="dark:bg-[#1a1a1c] dark:border-white/10">
                <SelectItem value="any" className="dark:text-white">Toutes les commandes</SelectItem>
                <SelectItem value="paid" className="dark:text-white">Payées</SelectItem>
                <SelectItem value="pending" className="dark:text-white">En attente</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadOrders} disabled={loadingOrders} className="dark:border-white/10 dark:text-white">
              <RefreshCw className={`w-4 h-4 ${loadingOrders ? 'animate-spin' : ''}`} />
            </Button>
          </motion.div>

          {/* Orders List */}
          <motion.div variants={itemVariants}>
            {loadingOrders ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#96bf48]" />
              </div>
            ) : orders.length === 0 ? (
              <Card className="dark:bg-[#111113] dark:border-white/10">
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-slate-300 dark:text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold dark:text-white mb-2">Aucune commande</h3>
                  <p className="text-slate-500 dark:text-zinc-400">Les commandes de votre boutique Shopify apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <Card key={order.id} className={`dark:bg-[#111113] dark:border-white/10 ${order.invoiced ? 'opacity-70' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${order.invoiced ? 'bg-green-100 dark:bg-green-900/20' : 'bg-[#96bf48]/10'}`}>
                            {order.invoiced ? (
                              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                            ) : (
                              <ShoppingBag className="w-6 h-6 text-[#96bf48]" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold dark:text-white">{order.order_number}</h3>
                              {getStatusBadge(order.financial_status)}
                              {order.invoiced && (
                                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                  Facturée
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-zinc-400">
                              <span>{order.customer_name}</span>
                              <span>•</span>
                              <span>{format(new Date(order.created_at), 'dd MMM yyyy', { locale: fr })}</span>
                              <span>•</span>
                              <span>{order.items.length} article(s)</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-lg font-bold dark:text-white">
                              {order.total.toFixed(2)} {order.currency}
                            </p>
                          </div>
                          {order.invoiced ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/app/invoices/${order.invoice_id}`)}
                              className="dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Voir
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => handleCreateInvoice(order.id)}
                              disabled={creatingInvoice === order.id}
                              className="bg-blue-600 hover:bg-blue-700 dark:bg-[#00F0FF] dark:text-black"
                              data-testid={`create-invoice-${order.id}`}
                            >
                              {creatingInvoice === order.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <FileText className="w-4 h-4 mr-1" />
                                  Facturer
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </motion.div>
        </>
      ) : (
        /* Not Connected State */
        <motion.div variants={itemVariants}>
          <Card className="dark:bg-[#111113] dark:border-white/10">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 rounded-full bg-[#96bf48]/10 flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-10 h-10 text-[#96bf48]" />
              </div>
              <h2 className="text-2xl font-bold dark:text-white mb-3">Connectez votre boutique Shopify</h2>
              <p className="text-slate-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">
                Importez automatiquement vos commandes Shopify et transformez-les en factures professionnelles en un clic.
              </p>
              <Button 
                onClick={() => setConnectDialogOpen(true)}
                className="bg-[#96bf48] hover:bg-[#7fa73d] text-white"
                size="lg"
              >
                <Link2 className="w-5 h-5 mr-2" />
                Connecter Shopify
              </Button>
              
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto text-left">
                <div className="p-4 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mb-3">
                    <ShoppingBag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="font-semibold dark:text-white mb-1">Import automatique</h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Vos commandes Shopify sont synchronisées automatiquement</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/20 flex items-center justify-center mb-3">
                    <FileText className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold dark:text-white mb-1">Facturation en 1 clic</h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Transformez une commande en facture instantanément</p>
                </div>
                <div className="p-4 bg-slate-50 dark:bg-[#1a1a1c] rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center mb-3">
                    <CheckCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="font-semibold dark:text-white mb-1">Suivi complet</h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400">Voyez quelles commandes ont déjà été facturées</p>
                </div>
              </div>
              
              <div className="mt-8">
                <Link to="/app/faq" className="text-blue-600 dark:text-[#00F0FF] hover:underline inline-flex items-center gap-1">
                  <HelpCircle className="w-4 h-4" />
                  Comment connecter mon Shopify ?
                </Link>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ShopifyPage;
